// Prototype mock data for the Quality KPI dashboard.
// Shapes mirror the backend response contracts documented in QA-7125–QA-7150
// so swapping in real API calls later is mechanical — see QA-7151.

const UNITS = [
  'Content Analysis', 'Error Analysis', 'Users & Retention', 'AI Analyst', 'VoC',
  'Channel Performance', 'Data Storage', 'Customer Journey Analysis', 'App Core Frontend',
  'Heap App', 'Tag Core', 'Core Cloud', 'Data Ingest', 'AI Setup', 'Quality Excellence',
];

const WEEKS = ['2025-36', '2025-37', '2025-38', '2025-39', '2025-40'];
const MONTHS = ['2025-06', '2025-07', '2025-08', '2025-09', '2025-10'];

// ---------------------------------------------------------------------------
// Production Incident (QA-7125, QA-7126, QA-7129, QA-7130, QA-7135)
// ---------------------------------------------------------------------------
export const productionIncident = {
  summary: { count: 38, previousCount: 47 },
  byPriority: [
    { bucket: 'P1 - Critical', count: 3 },
    { bucket: 'P2 - Major', count: 6 },
    { bucket: 'P3 - Medium', count: 24 },
    { bucket: 'P4 - Low', count: 5 },
  ],
  byIdentifiedBy: [
    { bucket: 'Alerting', count: 21 },
    { bucket: 'Dev team', count: 9 },
    { bucket: 'Support/Customer', count: 8 },
  ],
  priorityMonthly: MONTHS.map((month, i) => ({
    month,
    p1Critical: [0, 1, 0, 2, 0][i],
    p2Major: [1, 1, 2, 1, 1][i],
    p3Medium: [5, 6, 8, 7, 4][i],
    p4Low: [1, 2, 1, 1, 0][i],
  })),
  detailed: [
    { key: 'PI-6110', summary: 'experience monitoring page is missing section on API rules', reportedOn: '2025-09-24', reporter: 'Vitali Morgun', identifiedBy: 'Support/Customer', priority: 'P3 - Medium', status: 'PostMortem Done', escalationTrigger: 'None', labels: 'CS-Incident' },
    { key: 'PI-6068', summary: 'Latency in Azure US (CS)', reportedOn: '2025-09-10', reporter: 'Yen Hoang', identifiedBy: 'Dev team', priority: 'P2 - Major', status: 'PostMortem Done', escalationTrigger: 'Service Disruption', labels: 'CS-Incident' },
    { key: 'PI-6093', summary: 'Hotjar.com is down', reportedOn: '2025-09-19', reporter: 'Richya Acharya', identifiedBy: 'Alerting', priority: 'P1 - Critical', status: 'PostMortem Done', escalationTrigger: 'Service Disruption', labels: 'HJ-Incident' },
    { key: 'PI-6041', summary: 'Page Comparator - Data not loading under compare mode', reportedOn: '2025-09-03', reporter: 'Priyanka Ghosh', identifiedBy: 'Support/Customer', priority: 'P3 - Medium', status: 'PostMortem Done', escalationTrigger: 'None', labels: 'CS-Incident' },
    { key: 'PI-6021', summary: 'Session Replay ingestion delayed for EU region', reportedOn: '2025-08-28', reporter: 'Georges El Feghali', identifiedBy: 'Alerting', priority: 'P3 - Medium', status: 'Closed', escalationTrigger: 'None', labels: '' },
  ],
  byOrigin: {
    week: WEEKS.map((bucket, i) => ({ bucket, cs: [1, 3, 2, 1, 1][i], hj: [0, 2, 0, 1, 0][i], heap: [0, 0, 1, 0, 0][i] })),
    month: MONTHS.map((bucket, i) => ({ bucket, cs: [3, 4, 5, 3, 1][i], hj: [1, 2, 2, 1, 0][i], heap: [0, 1, 1, 0, 0][i] })),
    unit: UNITS.slice(0, 8).map((bucket, i) => ({ bucket, cs: 8 - i, hj: (8 - i) % 3, heap: (8 - i) % 2 })),
    priority: [
      { bucket: 'P1 - Critical', cs: 1, hj: 1, heap: 0 },
      { bucket: 'P2 - Major', cs: 3, hj: 2, heap: 1 },
      { bucket: 'P3 - Medium', cs: 9, hj: 4, heap: 2 },
      { bucket: 'P4 - Low', cs: 2, hj: 1, heap: 0 },
    ],
    component: ['CS - Other', 'CSQ - All', 'Session Replay', 'Data Connect', 'Integrations', 'VoC'].map((bucket, i) => ({
      bucket, cs: 6 - i, hj: (6 - i) % 3, heap: (6 - i) % 2,
    })),
  },
};

// ---------------------------------------------------------------------------
// Automation Coverage (QA-7131, QA-7132)
// ---------------------------------------------------------------------------
const epicRows = [
  { productGroup: 'Performance & Lifecycle', productUnit: 'Heap App', epic: '[PLA-10693] User Identification Promotion', epicStatus: 'GA', reporter: 'Luca Savarino', testCasesCreated: 4, testCasesReviewedPct: 100, automationCoveragePct: 100 },
  { productGroup: 'Performance & Lifecycle', productUnit: 'Users & Retention', epic: '[PLA-11839] In-context session replays', epicStatus: 'GA', reporter: 'Ismael Gonzalez Trujillo', testCasesCreated: 8, testCasesReviewedPct: 100, automationCoveragePct: 0 },
  { productGroup: 'Performance & Lifecycle', productUnit: 'Analysis Building', epic: '[PLA-15201] Contextual block widgets', epicStatus: 'GA', reporter: 'Vico Heidbrink', testCasesCreated: 1, testCasesReviewedPct: 100, automationCoveragePct: 0 },
  { productGroup: 'Mobile Analytics', productUnit: 'SDK Session Replay & Errors', epic: '[MOBILE-7971] User Identifier', epicStatus: 'GA', reporter: 'Jie Wang', testCasesCreated: 1, testCasesReviewedPct: 0, automationCoveragePct: 100 },
  { productGroup: 'Performance & Lifecycle', productUnit: 'Analysis Building', epic: '[PLA-10656] Crosswriting Configuration', epicStatus: 'GA', reporter: 'Ahmad Moussawi', testCasesCreated: 3, testCasesReviewedPct: 100, automationCoveragePct: 0 },
  { productGroup: 'Performance & Lifecycle', productUnit: 'Users & Retention', epic: '[PLA-12009] User lifecycle extension self service and trial', epicStatus: 'To Do', reporter: 'Luis Belmonte Diaz', testCasesCreated: 2, testCasesReviewedPct: 100, automationCoveragePct: 0 },
];

export const automationCoverage = {
  allEpics: { productUnitCount: 9, epicCount: 57, testCasesCreated: 334, testCasesReviewedPct: 96.41, automationCoveragePct: 73.65 },
  gaOrPendingReleaseTrain: { productUnitCount: 9, epicCount: 44, testCasesCreated: 257, testCasesReviewedPct: 98.05, automationCoveragePct: 69.26 },
  byEpicAll: epicRows,
  byEpicGaOrPendingReleaseTrain: epicRows.filter(r => r.epicStatus === 'GA'),
};

// ---------------------------------------------------------------------------
// Defect Severity Index (QA-7133, QA-7134, QA-7135, QA-7136, QA-7137)
// ---------------------------------------------------------------------------
const tierByUnit = UNITS.slice(0, 12).map((bucket, i) => {
  const critical = Math.max(0, 5 - Math.floor(i / 3));
  const major = Math.max(0, 8 - i);
  const moderate = Math.max(0, 4 - Math.floor(i / 2));
  const minor = Math.max(0, 10 - i);
  const weightedSum = critical * 5 + major * 3 + moderate * 2 + minor * 1;
  const dsi = Math.round((100 * Math.log10(weightedSum + 1) / Math.log10(8001)) * 100) / 100;
  return { bucket, critical, major, moderate, minor, dsi };
});

const tierByWeek = WEEKS.map((bucket, i) => {
  const critical = [0, 2, 22, 0, 1][i];
  const major = [1, 3, 31, 2, 3][i];
  const moderate = [0, 2, 3, 1, 2][i];
  const minor = [2, 5, 47, 6, 8][i];
  const weightedSum = critical * 5 + major * 3 + moderate * 2 + minor * 1;
  const dsi = Math.round((100 * Math.log10(weightedSum + 1) / Math.log10(8001)) * 100) / 100;
  return { bucket, critical, major, moderate, minor, dsi };
});

export const defectSeverityIndex = {
  topCounts: { productIncident: 61, bugsFromSupport: 354, bugsFromEpd: 137, totalBugsImpactedCustomers: 552 },
  tierCounts: { critical: 32, major: 65, moderate: 24, minor: 76 },
  dsi: 68.69,
  dsiByUnit: tierByUnit,
  dsiByWeek: tierByWeek,
  supByUnit: UNITS.slice(0, 6).map((bucket, i) => ({ bucket, p0Blocker: i % 2, p1High: 2, p2Medium: 8 - i, p3Low: 3 })),
  supByMonth: MONTHS.map((bucket, i) => ({ bucket, total: [171, 175, 168, 185, 7][i] })),
  supByWeek: WEEKS.map((bucket, i) => ({ bucket, total: [52, 34, 32, 41, 18][i] })),
  epdByUnit: UNITS.slice(0, 6).map((bucket, i) => ({ bucket, critical: i % 2, major: 3 - (i % 3), minor: 5 - i })),
  epdByMonth: MONTHS.map((bucket, i) => ({ bucket, total: [65, 97, 158, 194, 6][i] })),
  epdByWeek: WEEKS.map((bucket, i) => ({ bucket, total: [4, 10, 27, 141, 13][i] })),
};

// ---------------------------------------------------------------------------
// Quality Overview (QA-7139–QA-7150) — mostly composed from the sections above
// ---------------------------------------------------------------------------
export const qualityOverview = {
  topMetrics: {
    serviceDisruption: { count: 6, previousCount: 7 },
    dsi: { value: 57.47, previousValue: 49.82 },
    mttr: { hours: 19, previousHours: 88 },
    cfr: null, // TBD upstream — no chart to build against yet
    rdr: { ratio: 6.84, previousRatio: 14.75, target: 10 },
    functionalAutomationCoverage: null, // TBD upstream
    dlr: { ratio: 31.43, previousRatio: 29.29, target: 20 },
    ror: { ratio: 24.79, previousRatio: 20.0 },
  },
  serviceDisruption: {
    summary: { count: 6, previousCount: 7 },
    communicationToClients: { count: 5, previousCount: 7 },
    identifiedBy: [
      { bucket: 'Dev team', count: 4 },
      { bucket: 'Support/Customer', count: 2 },
    ],
    rootcause: [
      { bucket: 'Deploy Config Issue', count: 3 },
      { bucket: 'External Service Dependency', count: 1 },
      { bucket: 'Process Failure', count: 1 },
      { bucket: 'Bug', count: 1 },
    ],
    byUnit: UNITS.slice(0, 6).map(bucket => ({ bucket, count: 1 })),
    byOriginWeek: WEEKS.slice(1).map((bucket, i) => ({ bucket, cs: [1, 1, 2, 1][i], hj: [2, 0, 2, 0][i], heap: [0, 0, 0, 0][i] })),
    byOriginMonth: [{ bucket: '2025-09', cs: 3, hj: 3, heap: 0 }],
  },
  mttr: {
    summary: { hours: 19, previousHours: 88 },
    byUnit: [
      { bucket: 'Tag Core', mttrHours: 53 },
      { bucket: 'Core Cloud', mttrHours: 18 },
      { bucket: 'Channel Performance', mttrHours: 6 },
      { bucket: 'Conversion', mttrHours: 0 },
    ],
    byWeek: WEEKS.slice(0, 3).map((bucket, i) => ({ bucket, mttrHours: [29, 18, 0][i] })),
    byMonth: [{ bucket: '2025-09', mttrHours: 19 }],
    detailed: [
      { key: 'PI-6037', summary: 'VoC widgets disabled for some projects', mttrHours: 53 },
      { key: 'PI-6068', summary: 'Latency in Azure US (CS)', mttrHours: 18 },
      { key: 'PI-6041', summary: 'Page Comparator - Data not loading under compare mode', mttrHours: 6 },
      { key: 'PI-6093', summary: 'Hotjar.com is down', mttrHours: 0 },
    ],
  },
  defectLeakageRatio: {
    summary: { ratio: 31.43, previousRatio: 29.29, target: 20 },
    byWeek: WEEKS.map((bucket, i) => ({ bucket, ratio: [44.66, 14.75, 56.98, 52.50, 60.00][i] })),
    byUnit: UNITS.slice(0, 10).map((bucket, i) => ({
      bucket,
      supportCount: Math.max(0, 20 - i * 2),
      employeeCount: i > 6 ? 20 + i : Math.max(1, 3 - i),
      ratio: Math.max(0, 100 - i * 12),
    })),
  },
  requirementDefectRatio: {
    summary: { ratio: 6.84, previousRatio: 14.75, target: 10 },
    byUnit: [
      { bucket: 'PA Query Engine', ratio: 100 },
      { bucket: 'Segmentation', ratio: 33.33 },
      { bucket: 'Customer Journey Analysis', ratio: 28.57 },
      { bucket: 'Identity & Access Management', ratio: 14.29 },
      { bucket: 'SDK Cross-Platform Core', ratio: 12.50 },
      { bucket: 'Monetization Platform', ratio: 10.00 },
      { bucket: 'Product Offering Platform', ratio: 0 },
    ],
  },
  openServiceDisruption: [
    { productDomain: 'Analytics Intelligence', productGroup: 'Content & Experience Analysis', productUnit: 'Error Analysis', bugSummary: '[PI-6110] experience monitoring page is missing section on API rules', reportedOn: 'Sep 24, 2025', status: 'PostMortem Done', identifiedBy: 'Support/Customer', labels: 'CS-Incident', rootcause: 'Bug' },
    { productDomain: 'Core Tech', productGroup: 'Platform', productUnit: 'Core Cloud', bugSummary: '[PI-6068] Latency in Azure US (CS)', reportedOn: 'Sep 10, 2025', status: 'PostMortem Done', identifiedBy: 'Dev team', labels: 'CS-Incident', rootcause: 'External Service Dependency' },
    { productDomain: 'Analytics Intelligence', productGroup: 'Performance & Lifecycle Analytics', productUnit: 'Channel Performance', bugSummary: '[PI-6041] Page Comparator - Data not loading under compare mode', reportedOn: 'Sep 3, 2025', status: 'PostMortem Done', identifiedBy: 'Support/Customer', labels: 'CS-Incident', rootcause: 'Process Failure' },
  ],
  openSupportBugs: [
    { productDomain: null, productGroup: null, productUnit: 'Navigation & Design System', bugSummary: '[SUP-19803] Notification bell icon not showing unread state', priority: 'P3 - Medium', status: 'Open' },
    { productDomain: 'Analytics Intelligence', productGroup: 'Content & Experience Analysis', productUnit: 'Page Performance Analysis', bugSummary: '[SUP-19741] [Speed Analysis] - times out', priority: 'P3 - Medium', status: 'Open' },
  ],
};
